/**
 * Action types
 * @LOAD_ORDER : load actual order
 * @UPDATE_ORDER : update order
 * @INSERT_ORDER_PRODUCT : insert product in order items
 * @UPDATE_ORDER_PRODUCT : update a product of order items
 * @UPDATE_CUSTOMER_ADDRESS : update customer address
 * @HANDLE_DELIVERY_METHOD : handle delivery method type
 * @UPDATE_DELIVERY_FEE : update delivery fee value
 * @UPDATE_MERCHANT_ID : update merchant ID
 * @UPDATE_TOTAL : update total value
 * @UPDATE_CUSTOMER_INFOS : update customer infos
 * @UPDATE_CUSTOMER_PAYMENT : update customer payment
 * @UPDATE_OBSERVATION : update customer observation
 * @CLEAR_CART : clear cart after order complete
 */
export enum OrderTypes {
  LOAD_ORDER = '@order/LOAD_ORDER',
  UPDATE_ORDER = '@order/UPDATE_ORDER',
  INSERT_ORDER_PRODUCT = '@order/INSERT_ORDER_PRODUCT',
  UPDATE_ORDER_PRODUCT = '@order/UPDATE_ORDER_PRODUCT',
  UPDATE_ORDER_SHIP = '@order/UPDATE_ORDER_SHIP',
  UPDATE_ORDER_PRODUCT_AVAILABILITY = '@order/UPDATE_ORDER_PRODUCT_AVAILABILITY',
  UPDATE_ORDER_PRODUCT_STOCK = '@order/UPDATE_ORDER_PRODUCT_STOCK',
  UPDATE_ORDER_PRODUCT_CURRENT_STOCK = '@order/UPDATE_ORDER_PRODUCT_CURRENT_STOCK',
  UPDATE_ORDER_PRODUCT_AVAILABILITY_BY_CATEGORY_ID = '@order/UPDATE_ORDER_PRODUCT_AVAILABILITY_BY_CATEGORY_ID',
  UPDATE_ORDER_VARIATION_ITEM_AVAILABILITY = '@order/UPDATE_ORDER_VARIATION_ITEM_AVAILABILITY',
  UPDATE_ORDER_VARIATION_ITEM_STOCK = '@order/UPDATE_ORDER_VARIATION_ITEM_STOCK',
  UPDATE_CUSTOMER_ADDRESS = '@order/UPDATE_CUSTOMER_ADDRESS',
  HANDLE_DELIVERY_METHOD = '@order/HANDLE_DELIVERY_METHOD',
  UPDATE_DELIVERY_FEE = '@order/UPDATE_DELIVERY_FEE',
  UPDATE_MERCHANT_ID = '@order/UPDATE_MERCHANT_ID',
  UPDATE_TOTAL = '@order/UPDATE_TOTAL',
  UPDATE_CUSTOMER_INFOS = '@order/UPDATE_CUSTOMER_INFOS',
  UPDATE_CUSTOMER_PAYMENT = '@order/UPDATE_CUSTOMER_PAYMENT',
  UPDATE_OBSERVATION = '@order/UPDATE_OBSERVATION',
  CLEAR_CART = '@order/CLEAR_CART',
}

/**
 * @name : Name of customer
 * @email : Email of customer
 * @cpf : CPF of customer
 * @phoneDdd : DDD of phone number
 * @phoneNumber : Phone number of customer
 */

export interface Customer {
  id: string;
  name: string;
  email: string;
  cpf: string;
  phoneDdd: string;
  phoneNumber: string;
}

/**
 * @method : Delivery method
 * @address : Customer Address
 */
export interface Delivery {
  areaId: string;
  method: string;
  address: Address;
  ship?: Ship;
}

/**
 * @street : name of street
 * @streetNumber : number of address
 * @complement : an complement
 * @neighborhood : name of neighborhood
 * @city : name of city
 * @cityCode : city code
 * @state : name of state
 * @country : name of state
 * @postalCode : cep number
 * @reference : reference
 * @latitude : latitude
 * @longitude : longitude
 */

export interface Address {
  street: string;
  streetNumber: string;
  complement: string;
  neighborhood: string;
  city: string;
  cityCode: string;
  state: string;
  country: string;
  postalCode: string;
  reference: string;
  latitude: number;
  longitude: number;
}

export interface Variation {
  variationId: string;
  variationName: string;
  variationItemId: string;
  variationItemName: string;
  quantity: number;
  value: number;
  variationsGrid: boolean;
  variationItemAvailability: string;
  variationItemStockActive: boolean;
  variationItemStockCurrent: number;
  variationItemsMax: number;
}

/**
 * @item : name of item
 * @productId : id of item
 * @quantity : quantity of item
 * @price : price of item
 * @subtotal : subtotal of item
 * @discount : discount of item price
 * @addition : additional of item price
 * @total : total value of item
 * @observations : observations of item
 */

export interface Product {
  categoryId: string;
  availability: string;
  item: number;
  productId: string;
  productCode: string;
  productName: string;
  quantity: number;
  price: number;
  subtotal: number;
  discount: number;
  addition: number;
  total: number;
  stock: Stock;
  observations: string;
  variationsTotal: number;
  variations: Variation[];
}

export interface Stock {
  active: boolean;
  current: number;
  min: number;
  orderMax: number;
}

/**
 * @cardNumber : number of card
 * @expiryDate : expiration date of card
 * @securityCode : CVV
 * @holder : card owner
 * @cpf : CPF of card owner
 * @installments : number of value parts
 */

export interface CreditCard {
  cardNumber: string;
  expiryDate: string;
  securityCode: string;
  holder: string;
  cpf: string;
  installments: number;
}

/**
 * @code : code of payment, ex: "DIN" for money
 * @prepaid : online payment ? true
 * @value : value of payment
 * @changeFor : change of payment using DIN, value
 * @creditCard : credit card infos
 */

export interface Payment {
  code: string;
  prepaid: boolean;
  value: number;
  changeFor: number;
  creditCard?: CreditCard;
}

export interface OrderResponse {
  merchantId: string;
  id: string;
  shortId: string;
  status: string;
  customer: Customer;
  delivery: Delivery;
  preparationTime: number;
  observation: string;
  items: Product[];
  subtotal: number;
  deliveryFee: number;
  total: number;
  payments: [
    {
      code: string;
      description: string;
      prepaid: boolean;
      value: number;
      changeFor: number;
      cardTransaction: {
        authorizerMessage: string;
        customerReceipt: string;
        merchantReceipt: string;
        authorizerId: string;
        acquirerId: string;
        acquirerName: string;
        authorizerDate: string;
        authorizationNumber: string;
        esitefUsn: string;
        sitefUsn: string;
        hostUsn: string;
        paymentType: string;
        issuer: string;
      };
    },
  ];
  createdAt: string;
  updatedAt: string;
}

/**
 * State type
 * @data
 */
export interface OrderState {
  merchantId: string;
  customer: Customer;
  delivery: Delivery;
  observation: string;
  items: Product[];
  subtotal: number;
  deliveryFee: number;
  total: number;
  payments: Payment[];
}

export interface Ship {
  companyName: string;
  deliveryFee: number;
  deliveryTime: number;
  serviceCode: number;
  serviceName: string;
}

export interface ShipList {
  fromPostalCode: string;
  toPostalCode: string;
  services: Ship[];
}